import React, { useEffect, useMemo, useState } from 'react';
import { getJson } from '../api';
import '../styles/Inventory.css';

const STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

const formatCurrency = (value) =>
  Number.isFinite(value) ? `$${Number(value).toFixed(2)}` : '—';

const formatNumber = (value) =>
  Number.isFinite(value) ? Number(value).toLocaleString() : '—';

function Inventory() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState('');
  const [query, setQuery] = useState('');
  const [sizeFilter, setSizeFilter] = useState('all');

  useEffect(() => {
    let isMounted = true;
    async function fetchInventory() {
      setStatus(STATUS.LOADING);
      setErrorMessage('');
      try {
        const payload = await getJson('/inventory');
        const normalizedItems = Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload)
            ? payload
            : [];
        if (isMounted) {
          setItems(normalizedItems);
          setStatus(STATUS.SUCCESS);
        }
      } catch (error) {
        if (isMounted) {
          let message = 'Unable to load inventory right now.';
          if (error.status === 401) {
            message = 'Please sign in to view the inventory dashboard.';
          } else if (error.status === 403) {
            message = 'Admin access is required to view inventory.';
          }
          setItems([]);
          setErrorMessage(message);
          setStatus(STATUS.ERROR);
        }
      }
    }
    fetchInventory();
    return () => {
      isMounted = false;
    };
  }, []);

  const normalizedQuery = query.trim().toLowerCase();
  const visibleItems = useMemo(() => {
    return items.filter((item) => {
      const matchesSize =
        sizeFilter === 'all' || (item.size || '').toLowerCase() === sizeFilter;
      if (!matchesSize) {
        return false;
      }
      if (!normalizedQuery) {
        return true;
      }
      const haystack = `${item.product_number ?? ''} ${item.name ?? ''} ${
        item.description ?? ''
      }`.toLowerCase();
      return haystack.includes(normalizedQuery);
    });
  }, [items, normalizedQuery, sizeFilter]);

  const totalSkus = items.length;
  const totalUnits = items.reduce((sum, item) => {
    const value = Number(item.current_inventory);
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);

  const sizeOptions = useMemo(() => {
    const uniqueSizes = new Set();
    items.forEach((item) => {
      if (item.size) {
        uniqueSizes.add(item.size.toLowerCase());
      }
    });
    return Array.from(uniqueSizes).sort();
  }, [items]);

  const controlsDisabled = status !== STATUS.SUCCESS;

  return (
    <div className="inventory-page">
      <header className="inventory-header">
        <div>
          <p className="eyebrow">Internal tools</p>
          <h1>Inventory</h1>
          <p className="subhead">
            Monitor dealer-ready SKUs and spot restock needs at a glance.
          </p>
        </div>
        <div className="inventory-stats">
          <div className="stat-card">
            <p className="label">Total SKUs</p>
            <p className="value">{totalSkus}</p>
          </div>
          <div className="stat-card">
            <p className="label">Units On Hand</p>
            <p className="value">{formatNumber(totalUnits)}</p>
          </div>
        </div>
      </header>

      <section className="inventory-controls">
        <input
          type="search"
          placeholder="Search by product number, name, or description"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          disabled={controlsDisabled}
        />
        <select
          value={sizeFilter}
          onChange={(event) => setSizeFilter(event.target.value)}
          disabled={controlsDisabled}
        >
          <option value="all">All Sizes</option>
          {sizeOptions.map((size) => (
            <option key={size} value={size}>
              {size.toUpperCase()}
            </option>
          ))}
        </select>
      </section>

      {status === STATUS.LOADING && (
        <div className="inventory-banner">
          Loading the latest inventory...
        </div>
      )}
      {status === STATUS.ERROR && (
        <div className="inventory-banner error">
          {errorMessage}
        </div>
      )}

      <div className="inventory-table-wrapper">
        <table className="inventory-table">
          <thead>
            <tr>
              <th>Product #</th>
              <th>Name</th>
              <th>Size</th>
              <th>Thickness</th>
              <th>Dealer Price</th>
              <th>Inventory</th>
            </tr>
          </thead>
          <tbody>
            {status === STATUS.SUCCESS && visibleItems.map((item) => {
              const units = Number(item.current_inventory);
              const statusClass =
                Number.isFinite(units) && units <= 25
                  ? 'low'
                  : Number.isFinite(units)
                    ? 'healthy'
                    : 'unknown';
              return (
                <tr key={item.product_number}>
                  <td className="product-number">{item.product_number}</td>
                  <td>
                    <p className="item-name">{item.name}</p>
                    <p className="item-description">{item.description}</p>
                  </td>
                  <td>{item.size || '—'}</td>
                  <td>{item.thickness || '—'}</td>
                  <td>{formatCurrency(item.dealer_price)}</td>
                  <td>
                    <span className={`inventory-pill ${statusClass}`}>
                      {formatNumber(item.current_inventory)}
                    </span>
                  </td>
                </tr>
              );
            })}
            {status === STATUS.SUCCESS && visibleItems.length === 0 && (
              <tr>
                <td colSpan="6" className="empty-state">
                  No products match that search.
                </td>
              </tr>
            )}
            {status !== STATUS.SUCCESS && (
              <tr>
                <td colSpan="6" className="empty-state">
                  {status === STATUS.LOADING
                    ? 'Loading inventory...'
                    : 'Inventory is unavailable. Please try again later.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Inventory;
